// Bridge with the 51trips mobile app, which opens the trip book editor inside a
// react-native-webview with `?from=mobileApp`.

const EMBEDDED_QUERY_VALUE = 'mobileApp'
// The flag outlives the query string: in-app navigations (e.g. editor → review)
// drop `?from=mobileApp`, but the WebView keeps the same session storage.
const EMBEDDED_STORAGE_KEY = 'embeddedInMobileApp'

// The app draws a floating back button over the bottom-left corner of the page
// (20px inset + up to 44px wide on iOS). Bottom bars keep this much clearance on
// the left so they never sit under it, plus a 12px gap.
export const MOBILE_APP_BACK_BUTTON_CLEARANCE_PX = 20 + 44 + 12

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (message: string) => void }
  }
}

export const isEmbeddedInMobileApp = (): boolean => {
  if (typeof window === 'undefined') return false
  const fromQuery = new URLSearchParams(window.location.search).get('from') === EMBEDDED_QUERY_VALUE
  try {
    if (fromQuery) {
      window.sessionStorage.setItem(EMBEDDED_STORAGE_KEY, '1')
      return true
    }
    return window.sessionStorage.getItem(EMBEDDED_STORAGE_KEY) === '1'
  } catch {
    return fromQuery
  }
}

// Language the app asks the embedded page to use (`?from=mobileApp&lng=it`).
// Pure on the query string so it can run before i18next initialises; returns
// undefined outside the app or for an unsupported code.
export const getMobileAppLanguage = (
  search: string,
  supportedLngs: readonly string[]
): string | undefined => {
  const params = new URLSearchParams(search)
  if (params.get('from') !== EMBEDDED_QUERY_VALUE) return undefined
  const lng = params.get('lng')?.toLowerCase()
  return lng && supportedLngs.includes(lng) ? lng : undefined
}

// The app already pads the WebView below the status bar, so adding
// `env(safe-area-inset-top)` again when embedded doubles the header height.
export const safeAreaTopOffset = (px: number): string =>
  isEmbeddedInMobileApp() ? `${px}px` : `calc(env(safe-area-inset-top, 0px) + ${px}px)`

// Web → app messages. The app hides its floating back button while a full-screen
// sub-page (cover / page editors, preview) is open, since each has its own back.
export type MobileAppOutgoingMessage = { type: 'tripbookSubpage'; open: boolean }

export const postToMobileApp = (message: MobileAppOutgoingMessage) => {
  window.ReactNativeWebView?.postMessage(JSON.stringify(message))
}

// App → web messages: Android's hardware back asks the open sub-page to close.
export const CLOSE_SUBPAGE_MESSAGE_TYPE = 'tripbookCloseSubpage'

export const isCloseSubpageMessage = (data: unknown): boolean => {
  if (typeof data !== 'string') return false
  try {
    const parsed: unknown = JSON.parse(data)
    return (
      typeof parsed === 'object' &&
      parsed !== null &&
      (parsed as { type?: unknown }).type === CLOSE_SUBPAGE_MESSAGE_TYPE
    )
  } catch {
    return false
  }
}

// react-native-webview delivers app → web messages as `message` events on
// `window` (iOS) or `document` (Android); listen on both.
export const subscribeToMobileAppMessages = (listener: (data: unknown) => void) => {
  const handler = (event: Event) => listener((event as MessageEvent).data)
  window.addEventListener('message', handler)
  document.addEventListener('message', handler)
  return () => {
    window.removeEventListener('message', handler)
    document.removeEventListener('message', handler)
  }
}
